import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IconUser, IconLogout, IconShoppingBag, IconShield, IconMessage, IconChevronDown, IconUserPlus } from '@tabler/icons-react';
import { useAuth } from '../../../auth/AuthContext';
import { useCart } from '../../../contexts/CartContext';
import { useRole } from '../../../App';
import { getTotalUnreadCount } from '../../../services/messagingService';
import SiteInviteModal from '../../invite/SiteInviteModal';

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showResourcesDropdown, setShowResourcesDropdown] = useState(false);
  const resourcesTimeout = useRef(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showInvite, setShowInvite] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userProfile, logout } = useAuth();
  const { cartCount } = useCart();
  const { isAdmin, hasAdminAccess } = useRole();

  const isActive = (path) => location.pathname === path;
  const isResourcesActive = () => location.pathname.startsWith('/resources');

  useEffect(() => {
    if (!currentUser) { setUnreadCount(0); return; }
    const fetchUnreads = () => {
      getTotalUnreadCount(currentUser.uid).then(setUnreadCount).catch(() => {});
    };
    fetchUnreads();
    const interval = setInterval(fetchUnreads, 30000);
    return () => clearInterval(interval);
  }, [currentUser, location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Hover open/close only applies on devices that actually support hover.
  // On touch devices, taps fire mouseenter+click together which made the
  // dropdown flash open then immediately toggle closed.
  const supportsHover = typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover)').matches;

  const handleResourcesEnter = useCallback(() => {
    if (!supportsHover) return;
    clearTimeout(resourcesTimeout.current);
    setShowResourcesDropdown(true);
  }, [supportsHover]);

  const handleResourcesLeave = useCallback(() => {
    if (!supportsHover) return;
    resourcesTimeout.current = setTimeout(() => setShowResourcesDropdown(false), 150);
  }, [supportsHover]);

  const resourcesItems = [
    { path: '/resources/rules', label: 'Rules' },
    { path: '/resources/governing-bodies', label: 'Governing Bodies' },
    { path: '/resources/competitions', label: 'Competitions' },
  ];

  return (
    <nav className="uu-navigation">
      <section className="uu-nav-container">
        <Link to="/" className="uu-nav-logo">
          <img src="/UBUnitedlogoupdated.png" alt="UBunited Logo" className="uu-logo-image" />
        </Link>

        <section className={`uu-nav-menu ${mobileMenuOpen ? 'uu-active' : ''}`}>
          <Link
            to="/"
            className={`uu-nav-link ${isActive('/') ? 'uu-active' : ''}`}
            onClick={closeMobileMenu}
          >
            Pickleball
          </Link>

          <Link
            to="/blog"
            className={`uu-nav-link ${isActive('/blog') ? 'uu-active' : ''}`}
            onClick={closeMobileMenu}
          >
            Blog
          </Link>

          <Link
            to="/videos"
            className={`uu-nav-link ${isActive('/videos') ? 'uu-active' : ''}`}
            onClick={closeMobileMenu}
          >
            Videos
          </Link>

          <section
            className="uu-nav-dropdown-container"
            onMouseEnter={handleResourcesEnter}
            onMouseLeave={handleResourcesLeave}
          >
            <button
              className={`uu-nav-link uu-nav-dropdown-trigger ${isResourcesActive() ? 'uu-active' : ''}`}
              onClick={() => setShowResourcesDropdown(v => !v)}
            >
              Resources
              <IconChevronDown size={14} stroke={2} className="uu-dropdown-chevron" />
            </button>
            {showResourcesDropdown && (
              <section className="uu-nav-dropdown">
                {resourcesItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`uu-nav-dropdown-item ${isActive(item.path) ? 'uu-active' : ''}`}
                    onClick={() => { setShowResourcesDropdown(false); closeMobileMenu(); }}
                  >
                    {item.label}
                  </Link>
                ))}
              </section>
            )}
          </section>

          <Link
            to="/messaging"
            className={`uu-nav-link ${isActive('/messaging') ? 'uu-active' : ''}`}
            onClick={closeMobileMenu}
          >
            Messaging
            {unreadCount > 0 && <span className="uu-nav-badge">{unreadCount}</span>}
          </Link>

          <Link
            to="/groups"
            className={`uu-nav-link ${isActive('/groups') ? 'uu-active' : ''}`}
            onClick={closeMobileMenu}
          >
            Groups
          </Link>

          <Link
            to="/shop"
            className={`uu-nav-link ${isActive('/shop') ? 'uu-active' : ''}`}
            onClick={closeMobileMenu}
          >
            Shop
          </Link>

          {hasAdminAccess && (
            <Link
              to="/admin"
              className={`uu-nav-link ${isActive('/admin') ? 'uu-active' : ''}`}
              onClick={closeMobileMenu}
            >
              Admin
            </Link>
          )}
        </section>

        <section className="uu-nav-actions">
          {hasAdminAccess && (
            <Link to="/admin" className="uu-admin-link" aria-label="Admin Panel">
              <IconShield size={20} stroke={2} />
            </Link>
          )}

          <Link to="/shop?cart=1" className="uu-cart-btn" aria-label="View cart">
            <IconShoppingBag size={20} stroke={2} />
            {cartCount > 0 && <span className="uu-cart-badge">{cartCount}</span>}
          </Link>

          {currentUser ? (
            <section className="uu-user-menu-container">
              <button
                className="uu-user-greeting"
                onMouseEnter={() => setShowUserMenu(true)}
                onMouseLeave={() => setShowUserMenu(false)}
              >
                <span className="uu-greeting-text">
                  Hey, {userProfile?.firstName || 'Player'}
                </span>
                <IconUser className="uu-profile-icon-mobile" size={22} stroke={2} />
              </button>
              {showUserMenu && (
                <section
                  className="uu-user-dropdown"
                  onMouseEnter={() => setShowUserMenu(true)}
                  onMouseLeave={() => setShowUserMenu(false)}
                >
                  <Link to="/profile" className="uu-user-dropdown-item">
                    <IconUser size={18} />
                    <span>Profile</span>
                  </Link>
                  <Link to="/messaging" className="uu-user-dropdown-item">
                    <IconMessage size={18} />
                    <span>Messages</span>
                    {unreadCount > 0 && <span className="uu-dropdown-badge">{unreadCount}</span>}
                  </Link>
                  <button onClick={handleLogout} className="uu-user-dropdown-item">
                    <IconLogout size={18} />
                    <span>Sign Out</span>
                  </button>
                </section>
              )}
            </section>
          ) : (
            <>
              <Link to="/auth?mode=signup" className="uu-sign-in-btn uu-join-btn">Join UBunited</Link>
              <Link to="/auth" className="uu-sign-in-btn uu-sign-in-btn--outline">Sign In</Link>
            </>
          )}

          {currentUser && (
            <button
              className="uu-nav-invite-btn"
              onClick={() => setShowInvite(true)}
              aria-label="Send an invite"
              title="Send An Invite"
            >
              <IconUserPlus size={20} stroke={2} />
              <span className="uu-tooltip" aria-hidden="true">Send An Invite</span>
            </button>
          )}
        </section>

        <button
          className="uu-mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </section>

      {showInvite && <SiteInviteModal onClose={() => setShowInvite(false)} />}
    </nav>
  );
};

export default Navigation;
